import type { OverlayPanel } from "primereact/overlaypanel";
import { useCallback, useMemo, useRef, useState } from "react";
import type { MouseEvent } from "react";

import { capitalize } from "../../utils/text";
import {
  MONTH_NAMES,
  MONTH_SHORT,
  formatMonthRangeLabel,
  monthIndex,
  pad,
  stateOf,
  validateMonthRange,
} from "./month-range-picker.utils";
import type {
  MonthRangePickerProps,
  MonthRangePickerViewModel,
  MonthRangeValue,
  PeriodMonth,
} from "./types-month-range-picker";

/**
 * ViewModel do MonthRangePicker (Figma "Month Range Dual"): dois anos lado a lado, navegação de ano, seleção
 * em dois cliques (início, depois fim; um fim anterior ao início vira o novo início, então nunca sai de > até),
 * prévia ao passar o mouse e rótulo `mmm/aaaa – mmm/aaaa`; sem JSX.
 */
export function useMonthRangePicker({
  value,
  onChange,
  minYear,
  maxYear,
  disabled = false,
  invalid = false,
  id,
  name,
  testId,
  className,
  style,
  classNames,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
  onClick,
  ...rest
}: MonthRangePickerProps): MonthRangePickerViewModel {
  const panelRef = useRef<OverlayPanel>(null);
  const [firstYear, setFirstYear] = useState(value.from.year);
  const [anchor, setAnchor] = useState<PeriodMonth | null>(null);
  const [hover, setHover] = useState<PeriodMonth | null>(null);

  const clampYear = useCallback(
    (year: number) => {
      const upper = maxYear === undefined ? year : Math.min(year, maxYear - 1);
      return minYear === undefined ? upper : Math.max(upper, minYear);
    },
    [minYear, maxYear],
  );

  const toggle = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setFirstYear(clampYear(value.from.year));
      setAnchor(null);
      setHover(null);
      panelRef.current?.toggle(event);
      onClick?.(event);
    },
    [clampYear, value.from.year, onClick],
  );

  const isOutOfBounds = useCallback(
    (year: number) =>
      (minYear !== undefined && year < minYear) || (maxYear !== undefined && year > maxYear),
    [minYear, maxYear],
  );

  const pick = useCallback(
    (month: PeriodMonth) => {
      if (isOutOfBounds(month.year)) return;
      if (!anchor || monthIndex(month) < monthIndex(anchor)) {
        // Primeiro clique, ou fim anterior ao início: este mês vira o início.
        setAnchor(month);
        return;
      }
      setAnchor(null);
      setHover(null);
      onChange({ from: anchor, to: month });
      panelRef.current?.hide();
    },
    [anchor, isOutOfBounds, onChange],
  );

  const range = useMemo<MonthRangeValue>(() => {
    if (!anchor) return value;
    const end = hover && monthIndex(hover) >= monthIndex(anchor) ? hover : anchor;
    return { from: anchor, to: end };
  }, [anchor, hover, value]);

  const label = formatMonthRangeLabel(value);
  const error = validateMonthRange(value);
  const partTestId = (part: string) => (testId ? `${testId}-${part}` : undefined);

  const years = [0, 1].map((offset) => {
    const year = firstYear + offset;
    const arrow = offset === 0 ? ("previous" as const) : ("next" as const);
    return {
      year,
      arrow,
      testId: partTestId(`ano-${year}`),
      arrowLabel: arrow === "previous" ? "Ano anterior" : "Próximo ano",
      arrowIcon: arrow === "previous" ? ("chevron-left" as const) : ("chevron-right" as const),
      arrowTestId: partTestId(arrow === "previous" ? "anterior" : "proximo"),
      arrowDisabled:
        arrow === "previous"
          ? minYear !== undefined && firstYear <= minYear
          : maxYear !== undefined && firstYear + 1 >= maxYear,
      onArrow: () =>
        setFirstYear((current) => clampYear(current + (arrow === "previous" ? -1 : 1))),
      months: MONTH_SHORT.map((short, index) => {
        const month = { year, month: index + 1 };
        const state = stateOf(month, range);
        return {
          monthKey: `${year}-${pad(month.month)}`,
          label: capitalize(short),
          ariaLabel: `${capitalize(MONTH_NAMES[index] ?? "")} de ${year}`,
          state,
          pressed: state === "start" || state === "end" || state === "single",
          disabled: isOutOfBounds(year),
          testId: partTestId(`mes-${year}-${pad(month.month)}`),
          onPick: () => pick(month),
          onHover: () => setHover(month),
        };
      }),
    };
  });

  return {
    panelRef,
    label,
    panelClassName: classNames?.panel,
    panel: {
      years,
      error,
      panelTestId: partTestId("painel"),
      errorTestId: partTestId("erro"),
      onPanelLeave: () => setHover(null),
    },
    trigger: {
      id,
      name,
      disabled,
      toggle,
      testId: partTestId("abrir"),
      className: [className, classNames?.button].filter(Boolean).join(" ") || undefined,
      style,
      "aria-label": `Período: ${label}`,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid":
        invalid || error !== null || ariaInvalid === true || ariaInvalid === "true"
          ? true
          : undefined,
      "aria-required": ariaRequired,
      rest,
    },
  };
}
